import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import differenceBy from 'lodash.differenceby';
import classNames from 'classnames';
import produce from 'immer';
import { DndContext, MouseSensor, TouchSensor, useSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';

import PhotoSortable from './components/PhotoSortable';
import Button from '../../../../../../../../components/global/Button';
import CardElementPhotoSelectionControlGridSelect, {
  GRID_TYPES,
} from '../../../../../../../../services/cards/components/CardElementPhoto/CardElementPhotoSelectionControl/CardElementPhotoSelectionControlGridSelect';
import getAlbumSlideshowDesignerOrderPhotos from '../../../queries/getAlbumSlideshowDesignerOrderPhotos';
import { createLoadMoreMethod } from '../../../../../../../../util/apollo';
import Spinner from '../../../../../../../../components/global/Spinner';
import {
  albumPhotosDelete,
  albumPhotosHide,
} from '../../../../../../../../graphql/mutations/albums';
import { ALBUM_PHOTO_STATUS } from '../../../../../../../../constants/albums';

const propTypes = {
  loading: PropTypes.bool,
  hidePhotos: PropTypes.func.isRequired,
  deletePhotos: PropTypes.func.isRequired,
  slideshowPhotos: PropTypes.shape({
    total: PropTypes.number,
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        display_order: PropTypes.number,
        photo: PropTypes.shape({
          id: PropTypes.string,
        }),
      }),
    ),
  }),
  total: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

const PhotoOrderingOverlay = ({
  album,
  loading,
  hidePhotos,
  deletePhotos,
  slideshowPhotos,
  total,
  onClose,
  onChange,
  loadMore,
}) => {
  const [currentSlideshowPhotos, setCurrentSlideshowPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [gridType, setGridType] = useState(GRID_TYPES.SINGLE);

  useEffect(() => {
    if (slideshowPhotos?.length) {
      setCurrentSlideshowPhotos([
        // This allows to keep proper records of deleted or hidden photos.
        ...currentSlideshowPhotos.filter(
          slideshowPhoto =>
            !!slideshowPhotos.find(
              ({ photo }) => slideshowPhoto.photo.id === photo.id,
            ),
        ),
        // Add newly fetched photos to the current working photos if there's any
        ...differenceBy(slideshowPhotos, currentSlideshowPhotos, 'photo.id'),
      ]);
    }
  }, [slideshowPhotos?.length]);

  const onDone = async () => {
    setSaving(true);
    await onChange({
      slideshowPhotos: currentSlideshowPhotos,
    });
    setSaving(false);
    onClose();
  };

  const photoListClasses = classNames({
    'card-photo-selection-control-photo-list': true,
    'single-column': gridType === GRID_TYPES.SINGLE,
    'double-columns': gridType === GRID_TYPES.DOUBLE,
  });

  // Drag and Drop Context Related

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 50,
      tolerance: 5,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });
  const onDragEnd = ({ active, over }) => {
    if (!active || !over) {
      return;
    }

    if (active.id !== over.id) {
      setCurrentSlideshowPhotos(prevPhotos => {
        const oldIndex = prevPhotos.findIndex(
          ({ photo }) => photo.id === active.id,
        );
        const newIndex = prevPhotos.findIndex(
          ({ photo }) => photo.id === over.id,
        );
        return arrayMove(prevPhotos, oldIndex, newIndex);
      });
    }
  };

  return (
    <DndContext onDragEnd={onDragEnd} sensors={[mouseSensor, touchSensor]}>
      <div className="card-element-photo-selection-control-content">
        <div className="card-element-control-header">
          <Button
            btnStyle="link"
            className="text-extra-bold"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <span className="card-element-control-header-title">Edit Photos</span>
          <Button
            btnStyle="link"
            className="text-extra-bold"
            onClick={onDone}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Done'}
          </Button>
        </div>
        <CardElementPhotoSelectionControlGridSelect
          onGridTypeChange={newGridType => setGridType(newGridType)}
        />
        <ul className="text-sm text-muted my-2">
          <li>Hold and drag to reorder photos.</li>
          <li>Remove photos from the slideshow by hiding or deleting.</li>
        </ul>
        <SortableContext
          items={currentSlideshowPhotos.map(({ photo }) => photo.id)}
        >
          <div className="card-photo-selection-control-scroll">
            <div className={photoListClasses}>
              {currentSlideshowPhotos.map(record => {
                const photo = record.photo || record;

                return (
                  <PhotoSortable
                    key={photo.id}
                    photo={photo}
                    onHide={() =>
                      hidePhotos({ albumId: album.id, photo_ids: [photo.id] })
                    }
                    onDelete={() =>
                      deletePhotos({ albumId: album.id, photo_ids: [photo.id] })
                    }
                  />
                );
              })}
            </div>
          </div>
        </SortableContext>
        {loading && (
          <div className="d-flex w-100 align-items-center justify-content-center">
            <Spinner />
          </div>
        )}
        {loadMore && slideshowPhotos?.length < total && (
          <div className="card-photo-selection-control-photo-list-actions">
            <Button
              btnStyle="link"
              className="mb-2"
              onClick={async () => {
                setLoadingMore(true);
                await loadMore();
                setLoadingMore(false);
              }}
              btnBlock
              disabled={loadingMore || loading}
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </Button>
          </div>
        )}
      </div>
    </DndContext>
  );
};

PhotoOrderingOverlay.propTypes = propTypes;

const withCandidatePhotos = graphql(getAlbumSlideshowDesignerOrderPhotos, {
  options: ({ albumId }) => ({
    variables: {
      album_id: albumId,
    },
    // We will rely on Network Only as we always want to fetch the photo in the order they are last stored.
    fetchPolicy: 'network-only',
  }),
  props: ({ data: { album, fetchMore, loading } = {} }) => {
    return {
      loading,
      album,
      total: album?.slideshow?.photos?.total ?? 0,
      slideshowPhotos: album?.slideshow?.photos?.records ?? [],
      loadMore: createLoadMoreMethod({
        source: album,
        recordsPath: 'slideshow.photos',
        fetchMore,
      }),
    };
  },
  skip: ({ albumId }) => !albumId,
});

const withAlbumPhotosHide = graphql(albumPhotosHide, {
  props: ({ mutate, ownProps }) => ({
    hidePhotos: ({ albumId, photo_ids: photoIds }) =>
      mutate({
        variables: {
          input: {
            album: albumId,
            photo_ids: {
              select_all: false,
              selected_ids: photoIds,
              ignored_ids: [],
            },
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          albumPhotosHide: {
            __typename: 'AlbumPhotosHidePayload',
            photos: photoIds.map(id => ({
              id,
              album_photo_status: ALBUM_PHOTO_STATUS.UNPUBLISHED,
              thumbnail_urls: [],
              __typename: 'Photo',
            })),
          },
        },
        update: store => {
          const { album } = ownProps;
          const hiddenPhotosId = photoIds;

          const data = store.readQuery({
            query: getAlbumSlideshowDesignerOrderPhotos,
            variables: {
              album_id: album.id,
            },
          });

          const updatedData = produce(data, draft => {
            draft.album.slideshow.photos.total -= hiddenPhotosId.length;
            draft.album.slideshow.photos.count -= hiddenPhotosId.length;
            draft.album.slideshow.photos.records =
              data.album.slideshow.photos.records.filter(
                ({ photo }) => !hiddenPhotosId.includes(photo.id),
              );
          });

          store.writeQuery({
            query: getAlbumSlideshowDesignerOrderPhotos,
            variables: {
              album_id: album.id,
            },
            data: updatedData,
          });
        },
      }),
  }),
});

const withAlbumPhotosDelete = graphql(albumPhotosDelete, {
  props: ({ ownProps, mutate }) => ({
    deletePhotos: ({ albumId, photo_ids: photoIds }) =>
      mutate({
        variables: {
          input: {
            album: albumId,
            photo_ids: {
              select_all: false,
              selected_ids: photoIds,
              ignored_ids: [],
            },
            album_photos_delete_mode: 'ALBUM_PHOTOS_ONLY', // From here, We don't touch deleted photos.
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          albumPhotosDelete: {
            __typename: 'AlbumPhotosDeletePayload',
            success: true,
          },
        },
        update: store => {
          const { album } = ownProps;
          const deletedPhotosId = photoIds;

          const data = store.readQuery({
            query: getAlbumSlideshowDesignerOrderPhotos,
            variables: {
              album_id: album.id,
            },
          });

          const updatedData = produce(data, draft => {
            draft.album.slideshow.photos.total -= deletedPhotosId.length;
            draft.album.slideshow.photos.count -= deletedPhotosId.length;
            draft.album.slideshow.photos.records =
              data.album.slideshow.photos.records.filter(
                ({ photo }) => !deletedPhotosId.includes(photo.id),
              );
          });

          store.writeQuery({
            query: getAlbumSlideshowDesignerOrderPhotos,
            variables: {
              album_id: album.id,
            },
            data: updatedData,
          });
        },
      }),
  }),
});

export default compose(
  withCandidatePhotos,
  withAlbumPhotosHide,
  withAlbumPhotosDelete,
)(PhotoOrderingOverlay);
